import React, { useState, useEffect } from "react";
import {
  Search,
  RefreshCw,
  Download,
  DollarSign,
  AlertCircle,
} from "lucide-react";
import axios from "axios";

// Mock data for demonstration
const mockPayments = [];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusBadgeClass = (status) => {
  const classes = {
    completed: "bg-green-100 text-green-800",
    refunded: "bg-gray-100 text-gray-800",
    pending: "bg-yellow-100 text-yellow-800",
    failed: "bg-red-100 text-red-800",
    expired: "bg-orange-100 text-orange-800"
  };
  return classes[status] || "bg-gray-100 text-gray-800";
};

const getStatusLabel = (status) => {
  const labels = {
    completed: "Réussi",
    refunded: "Remboursé",
    pending: "En attente",
    failed: "Échec",
    expired: "Expiré"
  };
  return labels[status] || status;
};

const exportToCSV = (data, filename) => {
  const headers = ["ID", "Client", "Email", "Produit", "Montant", "Devis", "Date", "Statut"];
  const rows = data.map(p => [
    p.payment_intent_id,
    p.customer_name,
    p.customer_email,
    p.product_name,
    p.amount,
    p.quote_id || 'N/A',
    formatDate(p.created_at),
    getStatusLabel(p.status)
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.csv`;
  link.click();
};

const API_BASE_URL = "https://api.greenecobagsusa.com/api";

const PaymentAdminPanel = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [processingRefund, setProcessingRefund] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const applyFilters = (data, searchTerm, statusFilter) => {
    let result = [...data];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (payment) =>
          payment.customer_email?.toLowerCase().includes(searchLower) ||
          payment.customer_name?.toLowerCase().includes(searchLower) ||
          payment.payment_intent_id?.toLowerCase().includes(searchLower) ||
          payment.product_name?.toLowerCase().includes(searchLower)
      );
    }

    if (statusFilter) {
      result = result.filter((payment) => payment.status === statusFilter);
    }

    setFilteredPayments(result);
    setTotalPages(Math.ceil(result.length / perPage));
    setCurrentPage(1);
  };

  const handleCopyId = (id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError("Token d'authentification manquant");
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/payments`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success && response.data.payments) {
        const paymentsData = response.data.payments.map(payment => ({
          id: payment.id,
          payment_intent_id: payment.payment_intent_id || payment.payment_id,
          customer_name: payment.customer_name,
          customer_email: payment.customer_email,
          product_name: payment.product_name,
          amount: (payment.amount / 100).toFixed(3),
          quote_id: payment.quote_id,
          status: payment.status || payment.payment_status,
          created_at: payment.created_at
        }));

        setPayments(paymentsData);
        setFilteredPayments(paymentsData);
      } else {
        setPayments([]);
        setFilteredPayments([]);
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
      setPayments([]);
      setFilteredPayments([]);

      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
      } else {
        setError("Erreur lors du chargement des paiements");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  useEffect(() => {
    applyFilters(payments, search, status);
  }, [search, status, perPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters(payments, search, status);
  };

  const handleRefund = async (e) => {
    e.preventDefault();
    if (!selectedPayment) return;

    setProcessingRefund(true);
    // Simulate API call
    setTimeout(() => {
      setShowRefundModal(false);
      setSelectedPayment(null);
      setProcessingRefund(false);
      alert('Remboursement effectué avec succès');
    }, 1500);
  };

  const openRefundModal = (payment) => {
    setSelectedPayment(payment);
    setShowRefundModal(true);
  };

  const handleExport = () => {
    exportToCSV(filteredPayments, 'payments-export');
  };

  const paginatedPayments = filteredPayments.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  return (
    <div className="min-h-screen bg-gray-50 px-2 sm:px-4 lg:px-6 py-4 sm:py-8 lg:ml-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-900">
          Payment Management
        </h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded mb-4 flex items-start">
            <AlertCircle className="mr-2 flex-shrink-0 mt-0.5" size={18} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 md:p-6 mb-4 sm:mb-6">
          <form onSubmit={handleSearch} className="space-y-3 sm:space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Research
                </label>
                <div className="relative">
                  <input
                    type="text"
                    className="pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Email, nom, ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <Search
                    className="absolute left-2.5 sm:left-3 top-2.5 text-gray-400"
                    size={16}
                  />
                </div>
              </div>

              <div className="w-full sm:w-48">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Statuts
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="completed">Successful</option>
                  <option value="refunded">Refunded</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">


              <button
                type="button"
                onClick={fetchPayments}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center justify-center transition-colors text-sm"
              >
                <RefreshCw className="mr-1" size={16} />
                Refresh
              </button>

              <button
                type="button"
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center transition-colors text-sm"
                onClick={handleExport}
              >
                <Download className="mr-1" size={16} />
                Export
              </button>
            </div>
          </form>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quote
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th> */}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      <div className="flex justify-center items-center">
                        <RefreshCw className="animate-spin mr-2" size={18} />
                        Loading...
                      </div>
                    </td>
                  </tr>
                ) : paginatedPayments.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      No payment found
                    </td>
                  </tr>
                ) : (
                  paginatedPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <span
                          className="text-xs cursor-pointer hover:text-blue-600 transition-colors relative"
                          onClick={() => handleCopyId(payment.payment_intent_id)}
                          title={payment.payment_intent_id || "N/A"}
                        >
                          {payment.payment_intent_id
                            ? payment.payment_intent_id.substring(0, 12) + "..."
                            : "N/A"}
                          {copiedId === payment.payment_intent_id && (
                            <span className="absolute -top-8 left-0 bg-green-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              Copié!
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>{payment.customer_name}</div>
                        <div className="text-xs text-gray-400">
                          {payment.customer_email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payment.product_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payment.amount}$
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payment.quote_id ? `#${payment.quote_id}` : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(payment.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                            payment.status
                          )}`}
                        >
                          {getStatusLabel(payment.status)}
                        </span>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => openRefundModal(payment)}
                          disabled={payment.status === "refunded"}
                          className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white ${payment.status === "refunded"
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-red-600 hover:bg-red-700 transition-colors"
                            }`}
                        >
                          <DollarSign className="mr-1" size={14} />
                          Refund
                        </button>
                      </td> */}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-3">
          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="flex justify-center items-center">
                <RefreshCw className="animate-spin mr-2" size={18} />
                <span className="text-sm text-gray-500">Chargement...</span>
              </div>
            </div>
          ) : paginatedPayments.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <p className="text-sm text-gray-500">Aucun paiement trouvé</p>
            </div>
          ) : (
            paginatedPayments.map((payment) => (
              <div
                key={payment.id}
                className="bg-white rounded-lg shadow-md p-4 space-y-3"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm mb-1 truncate">
                      {payment.customer_name}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">{payment.customer_email}</p>
                  </div>
                  <span
                    className={`ml-2 flex-shrink-0 px-2 py-1 text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                      payment.status
                    )}`}
                  >
                    {getStatusLabel(payment.status)}
                  </span>
                </div>

                <div className="border-t border-gray-200 pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Produit:</span>
                    <span className="text-gray-900 font-medium text-right ml-2 flex-1 truncate">
                      {payment.product_name}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Montant:</span>
                    <span className="text-gray-900 font-semibold">
                      {payment.amount} $
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Devis:</span>
                    <span className="text-gray-900">
                      {payment.quote_id ? `#${payment.quote_id}` : "N/A"}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Date:</span>
                    <span className="text-gray-900">
                      {formatDate(payment.created_at)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-gray-500">ID:</span>
                    <span
                      className="text-xs text-gray-900 font-mono ml-2 cursor-pointer hover:text-blue-600 transition-colors relative flex-shrink-0"
                      onClick={() => handleCopyId(payment.payment_intent_id)}
                      title={payment.payment_intent_id || "N/A"}
                    >
                      {payment.payment_intent_id
                        ? payment.payment_intent_id.substring(0, 17) //+ "..."
                        : "N/A"}
                      {copiedId === payment.payment_intent_id && (
                        <span className="absolute -top-8 right-0 bg-green-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                          Copié!
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        {/* Pagination */}
        <div div className="mt-4 bg-white rounded-lg shadow-md px-3 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4" >
          <div className="flex flex-col sm:flex-row items-center gap-2 text-xs sm:text-sm">
            <span className="text-gray-700 text-center sm:text-left">
              Affichage de{" "}
              <span className="font-medium">{paginatedPayments.length}</span> sur{" "}
              <span className="font-medium">{filteredPayments.length}</span> paiements
            </span>
            <select
              className="px-2 py-1 border border-gray-300 rounded-md text-xs sm:text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={perPage}
              onChange={(e) => {
                setPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>

          <div className="flex gap-1 sm:gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-2 sm:px-3 py-1 border rounded-md transition-colors text-xs sm:text-sm ${currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              Préc.
            </button>

            <span className="px-2 sm:px-4 py-1 text-xs sm:text-sm flex items-center">
              Page {currentPage} / {totalPages || 1}
            </span>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`px-2 sm:px-3 py-1 border rounded-md transition-colors text-xs sm:text-sm ${currentPage === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              Suiv.
            </button>
          </div>
        </div>

        {/* Refund Modal */}
        {showRefundModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
              <div
                className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                onClick={() => !processingRefund && setShowRefundModal(false)}
              />

              <div className="inline-block w-full max-w-lg align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <DollarSign className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                      <h3 className="text-base sm:text-lg leading-6 font-medium text-gray-900">
                        Remboursement
                      </h3>
                      <div className="mt-2">
                        <p className="text-xs sm:text-sm text-gray-500">
                          Vous êtes sur le point de rembourser le paiement :
                        </p>
                        <div className="mt-2 p-3 border border-gray-200 rounded-md space-y-1">
                          <p className="text-sm font-medium">
                            {selectedPayment?.product_name}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-500">
                            Client : {selectedPayment?.customer_name}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-500 break-all">
                            {selectedPayment?.customer_email}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-500">
                            Montant : {selectedPayment?.amount} $
                          </p>
                          {selectedPayment?.quote_id && (
                            <p className="text-xs sm:text-sm text-gray-500">
                              Devis : #{selectedPayment?.quote_id}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="mt-4">
                        <p className="text-xs sm:text-sm text-gray-500">
                          Le remboursement sera effectué pour le montant total du
                          paiement. Voulez-vous continuer?
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <button
                    type="button"
                    className="w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    onClick={() => setShowRefundModal(false)}
                    disabled={processingRefund}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className={`w-full sm:w-auto inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors ${processingRefund ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    onClick={handleRefund}
                    disabled={processingRefund}
                  >
                    {processingRefund ? "Traitement..." : "Rembourser"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentAdminPanel;